// Remember to not import editor stuff in here as this needs to be used outside of lazy loads
import type { Editor } from '@tiptap/react';
import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Stores the ref to the editor for convenient access outside
 * the main editor tree.
 */
export const EditorContext = createContext<{
  ref?: Editor | undefined;
  setRef?: (ref) => void;
  isReady: boolean;
  setIsReady: (value: boolean) => void;
  isEmpty: boolean;
  setIsEmpty: (value: boolean) => void;
  getStateForSave: () => { version: string; document: any };
}>({
  isReady: false,
  isEmpty: true,
  setIsReady: () => null,
  setIsEmpty: () => null,
  getStateForSave: () => ({
    version: '1',
    document: undefined,
  }),
});

/**
 * Hook to access the current editor ref
 */
export const useEditorContext = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error(
      'useEditorContext must be used within a EditorContextProvider'
    );
  }
  return context.ref;
};

/**
 * Wrap your components that need editor api access
 *
 * Important, use once per instance
 */
export const EditorContextProvider: React.FC<any> = ({ children }) => {
  const [ref, setRef] = useState<Editor | undefined>();
  const [isReady, setIsReady] = useState(false);
  // We have to track isEmpty separately because the editor object doesn't cause context updates as editor content changes
  const [isEmpty, setIsEmpty] = useState(true);

  const value = useMemo(
    () => ({
      ref,
      setRef,
      isReady,
      setIsReady,
      isEmpty,
      setIsEmpty,
      getStateForSave: () => ({
        version: '1',
        document: ref?.getJSON() ?? {},
      }),
    }),
    [ref, isReady, isEmpty]
  );
  return (
    <EditorContext.Provider value={value}>{children}</EditorContext.Provider>
  );
};

/**
 * Stores the measured container size
 */
export const EditorContainerSizeContext = createContext<{
  containerSize?: number;
  setContainerSize?: (size: number) => void;
}>({});

/**
 *  Container size provider
 */
export const EditorContainerSizeContextProvider: React.FC<any> = ({
  children,
}) => {
  const [containerSize, setContainerSize] = useState<number>();
  const value = useMemo(
    () => ({ containerSize, setContainerSize }),
    [containerSize]
  );
  return (
    <EditorContainerSizeContext.Provider value={value}>
      {children}
    </EditorContainerSizeContext.Provider>
  );
};

/**
 * Hook to access the current editor ref
 */
export const useEditorContainerSize = () => {
  const context = useContext(EditorContainerSizeContext);
  if (!context) {
    throw new Error(
      'useEditorContainerSize must be used within a EditorContainerSizeContextProvider'
    );
  }
  return context.containerSize;
};

/**
 * Wrap your editors in this
 */
export const EditorContextProviderWrapper: React.FC<any> = ({ children }) => {
  return (
    <EditorContextProvider>
      <EditorContainerSizeContextProvider>
        {children}
      </EditorContainerSizeContextProvider>
    </EditorContextProvider>
  );
};
